import { forwardRef } from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Mail, MapPin } from "lucide-react";
import logo from "@/assets/fpf3-logo-new.png";

const footerLinks = {
  institucional: [{
    label: "A Federação",
    href: "/federacao"
  }, {
    label: "Diretoria",
    href: "/federacao#diretoria"
  }, {
    label: "Estatuto",
    href: "/federacao#estatuto"
  }, {
    label: "Transparência",
    href: "/transparencia"
  }, {
    label: "CBFF",
    href: "https://cbff.com.br",
    external: true
  }],
  esporte: [{
    label: "Rankings",
    href: "/rankings"
  }, {
    label: "Eventos",
    href: "/eventos"
  }, {
    label: "Chamamento 2027",
    href: "/chamamento-2027"
  }, {
    label: "Boxes Afiliados",
    href: "/boxes"
  }, {
    label: "Regras",
    href: "/federacao#regras"
  }],
  comunidade: [{
    label: "Notícias",
    href: "/noticias"
  }, {
    label: "Filiação",
    href: "/filiacao"
  }, {
    label: "Contato",
    href: "#contato"
  }],
  legal: [{
    label: "Política de Privacidade",
    href: "/politica-de-privacidade"
  }, {
    label: "Termos de Uso",
    href: "/termos-de-uso"
  }, {
    label: "Políticas de Ingressos",
    href: "/politicas-ingressos"
  }]
};

const socialLinks = [{
  icon: Instagram,
  href: "https://www.instagram.com/federacaopbf3/",
  label: "Instagram"
}, {
  icon: Facebook,
  href: "https://facebook.com/fpf3pb",
  label: "Facebook"
}, {
  icon: Youtube,
  href: "https://youtube.com/fpf3pb",
  label: "YouTube"
}];

const Footer = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
  function FooterInner(props, ref) {
    return (
      <footer ref={ref} className="bg-fpf3-black border-t border-border/50 shadow-[0_-1px_20px_hsl(0_84%_50%/0.05)]" {...props}>
      {/* Main Footer */}
      <div className="container mx-auto px-4 py-14 md:py-20">
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-5 gap-6 md:gap-8 lg:gap-12">
          {/* Brand */}
          <div className="col-span-2 lg:col-span-2">
            <Link to="/" className="inline-block mb-4">
              <img alt="FPF3" className="h-16 md:h-20 w-auto" src={logo} width={1920} height={1525} loading="lazy" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-sm">
              Federação Paraibana de Funcional Fitness – regulamentando, 
              organizando e promovendo o Funcional Fitness no estado da Paraíba.
            </p>
            <div className="flex gap-2 md:gap-3">
              {socialLinks.map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="w-11 h-11 md:w-10 md:h-10 bg-white/5 border border-white/10 rounded-full flex items-center justify-center text-white/70 hover:bg-primary hover:text-white hover:border-primary hover:-translate-y-1 transition-all duration-300 min-w-[44px] min-h-[44px]" aria-label={social.label}>
                  <social.icon size={18} />
                </a>)}
            </div>

            {/* Newsletter */}
            <div className="mt-6">
              <p className="text-[10px] uppercase tracking-[0.2em] text-white/60 mb-2 font-semibold">Receba novidades</p>
              <div className="flex gap-2">
                <input
                  type="email"
                  placeholder="Seu email"
                  className="bg-white/5 border border-white/10 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-white/30 flex-1 focus:outline-none focus:border-primary/50 transition-colors min-w-0"
                />
                <button className="bg-primary text-white px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider hover:bg-primary/90 transition-colors shrink-0">
                  OK
                </button>
              </div>
            </div>
          </div>

          {/* Institucional */}
          <div>
            <h3 className="font-bold uppercase tracking-[0.15em] text-sm mb-4">
              Institucional
            </h3>
            <ul className="space-y-2">
              {footerLinks.institucional.map(link => <li key={link.label}>
                  {link.external ? <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary text-sm transition-colors">
                      {link.label}
                    </a> : <Link to={link.href} className="text-muted-foreground hover:text-primary text-sm transition-colors">
                      {link.label}
                    </Link>}
                </li>)}
            </ul>
          </div>

          {/* Esporte */}
          <div>
            <h3 className="font-bold uppercase tracking-[0.15em] text-sm mb-4">
              Esporte
            </h3>
            <ul className="space-y-2">
              {footerLinks.esporte.map(link => <li key={link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Comunidade */}
          <div>
            <h3 className="font-bold uppercase tracking-[0.15em] text-sm mb-4">
              Comunidade
            </h3>
            <ul className="space-y-2">
              {footerLinks.comunidade.map(link => <li key={link.label}>
                  <Link to={link.href} className="text-muted-foreground hover:text-primary text-sm transition-colors">
                    {link.label}
                  </Link>
                </li>)}
            </ul>
            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <Mail size={14} />
                <span>federacaopbf3@gmail.com</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground text-sm">
                <MapPin size={14} />
                <span>João Pessoa, PB</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-border">
        <div className="container mx-auto px-4 py-5 md:py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-4 text-center md:text-left">
            <p className="text-muted-foreground text-xs leading-relaxed">
              © 2026 FPF3 – Federação Paraibana de Funcional Fitness. Todos os direitos reservados.
              <br className="md:hidden" />
              <span className="ml-1">CNPJ: 65.974.896/0001-55</span>
            </p>
            <p className="text-muted-foreground text-xs">
              Filiada à{" "}
              <a href="https://cbff.com.br" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline min-h-[44px] inline-flex items-center">
                Confederação Brasileira de Funcional Fitness
              </a>
            </p>
            <div className="flex items-center gap-4 text-xs">
              {footerLinks.legal.map((link) => (
                <Link key={link.label} to={link.href} className="text-muted-foreground hover:text-primary transition-colors">
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = "Footer";

export default Footer;
